import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { SignageListedPerson } from './signagePeople';

const ROW_HEIGHT = 54;
const MIN_COLUMN_WIDTH = 300;
const COLUMN_GAP = 48;
const PAGE_INTERVAL_MS = 10_000;
const HIGHLIGHT_MS = 8_000;

// A person picked from the search bar; the nonce re-triggers a repeat pick.
export interface SignageListFocus {
  key: string;
  nonce: number;
}

interface Props {
  heading: string;
  people: SignageListedPerson[];
  // Shown in place of the grid: loading, names hidden, nobody to list
  message: string | null;
  focus: SignageListFocus | null;
  showSafetyRoles: boolean;
}

/**
 * A grid of people for the list boards. Pages hold as many rows and columns
 * as fit the screen and cycle on a timer when the list doesn't fit on one.
 */
export function SignagePeopleList({ heading, people, message, focus, showSafetyRoles }: Props) {
  const gridAreaRef = useRef<HTMLDivElement | null>(null);
  const [area, setArea] = useState({ width: 0, height: 0 });
  const [page, setPage] = useState(0);
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const [handledFocus, setHandledFocus] = useState<SignageListFocus | null>(null);

  useLayoutEffect(() => {
    const element = gridAreaRef.current;
    if (!element) return;
    const measure = () => setArea({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const columns = Math.max(1, Math.floor((area.width + COLUMN_GAP) / (MIN_COLUMN_WIDTH + COLUMN_GAP)));
  const rows = Math.max(1, Math.floor(area.height / ROW_HEIGHT));
  const perPage = columns * rows;
  const pageCount = Math.max(1, Math.ceil(people.length / perPage));
  // The list shrinks between polls; stay on the last page rather than a blank one.
  const currentPage = Math.min(page, pageCount - 1);
  const pagePeople = people.slice(currentPage * perPage, (currentPage + 1) * perPage);
  // Names run down each column; a short page is balanced across the columns
  // instead of filling the first one and leaving the rest empty.
  const rowsOnPage = Math.ceil(pagePeople.length / columns);

  // A colleague picked from the search bar: jump to their page and highlight
  // them. Handled during render so the jump uses the list as it is now.
  if (focus && focus !== handledFocus) {
    setHandledFocus(focus);
    const index = people.findIndex(p => p.key === focus.key);
    if (index >= 0) {
      setPage(Math.floor(index / perPage));
      setHighlightKey(focus.key);
    }
  }

  useEffect(() => {
    if (highlightKey === null) return;
    const timeout = window.setTimeout(() => setHighlightKey(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timeout);
  }, [highlightKey, handledFocus]);

  // Paging holds still while someone is highlighted.
  useEffect(() => {
    if (pageCount <= 1 || highlightKey !== null) return;
    const timeout = window.setTimeout(() => setPage((currentPage + 1) % pageCount), PAGE_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [currentPage, pageCount, highlightKey]);

  return (
    <Wrapper>
      <Heading>{heading}</Heading>
      <GridArea ref={gridAreaRef}>
        {message !== null ? (
          <Message>{message}</Message>
        ) : (
          <Grid key={currentPage} $columns={columns} $rows={rowsOnPage}>
            {pagePeople.map(person => (
              <PersonRow key={person.key} $highlighted={person.key === highlightKey}>
                <Avatar>{person.initials}</Avatar>
                <PersonText>
                  <PersonName>{person.name}</PersonName>
                  {person.detail && <PersonDetail>{person.detail}</PersonDetail>}
                </PersonText>
                {showSafetyRoles && person.firstAider && (
                  <RoleIcon src="/assets/icons/first-aider.svg" alt="First aider" title="First aider" />
                )}
                {showSafetyRoles && person.fireWarden && (
                  <RoleIcon src="/assets/icons/fire-warden.svg" alt="Fire warden" title="Fire warden" />
                )}
              </PersonRow>
            ))}
          </Grid>
        )}
      </GridArea>
      <Footer>
        {message === null && pageCount > 1 && Array.from({ length: pageCount }, (_, i) => (
          <DotButton
            key={i}
            onClick={() => {
              setPage(i);
              setHighlightKey(null);
            }}
            aria-label={`Page ${i + 1} of ${pageCount}`}
          >
            <Dot $active={i === currentPage} />
          </DotButton>
        ))}
      </Footer>
    </Wrapper>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// The bottom padding keeps the last row of names clear of the Powered by
// badge in the corner.
const Wrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 24px 48px 32px;
  background: white;
`;

const Heading = styled.div`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: ${Colours.black};
  margin-bottom: 16px;
`;

const GridArea = styled.div`
  flex: 1;
  min-height: 0;
`;

const Grid = styled.div<{ $columns: number; $rows: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$columns}, minmax(0, 1fr));
  grid-template-rows: repeat(${p => p.$rows}, ${ROW_HEIGHT}px);
  grid-auto-flow: column;
  column-gap: ${COLUMN_GAP}px;
  animation: ${fadeIn} 400ms ease;
`;

const PersonRow = styled.div<{ $highlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin: 3px -8px;
  padding: 0 8px;
  border-radius: 8px;
  background: ${p => p.$highlighted ? Colours.blue10 : 'transparent'};
  box-shadow: ${p => p.$highlighted ? `inset 0 0 0 2px ${Colours.blue}` : 'none'};
  transition: background 200ms ease, box-shadow 200ms ease;
`;

const Avatar = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${Colours.veryDarkBlue};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 12px;
`;

const PersonText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PersonName = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: ${Colours.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonDetail = styled.span`
  font-family: "proxima-nova", sans-serif;
  font-size: 13px;
  line-height: 16px;
  color: ${Colours.darkGrey};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoleIcon = styled.img`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: block;
`;

const Message = styled.div`
  font-family: "proxima-nova", sans-serif;
  font-size: 18px;
  color: ${Colours.darkGrey};
`;

const Footer = styled.div`
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DotButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Dot = styled.span<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$active ? Colours.blue : Colours.blue20};
  transition: background 200ms ease;
`;
